import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import * as api from "../services/api";
import {
  CategoryOptionDto,
  UpdateCategoryOptionDto,
  OptionType,
} from "../types";
import Layout from "../components/Layout";
import { UNIT_OPTIONS } from "../constants/units";

const CATEGORY_TYPES: {
  value: OptionType;
  label: string;
  description: string;
}[] = [
  {
    value: "accommodation",
    label: "Noclegi",
    description: "Pokoje hotelowe i miejsca noclegowe",
  },
  {
    value: "gastronomy",
    label: "Gastronomia",
    description: "Posiłki, catering i usługi gastronomiczne",
  },
  {
    value: "conference",
    label: "Konferencje",
    description: "Sale konferencyjne i wyposażenie",
  },
  {
    value: "additional",
    label: "Dodatki",
    description: "Dodatkowe usługi i udogodnienia",
  },
];

const CategoryOptionsManagement = () => {
  const [selectedType, setSelectedType] = useState<OptionType>("accommodation");
  const [options, setOptions] = useState<CategoryOptionDto[]>([]);
  const [editingOption, setEditingOption] = useState<CategoryOptionDto | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [optionToDeactivate, setOptionToDeactivate] =
    useState<CategoryOptionDto | null>(null);
  // const dropdownRef = useRef<HTMLDivElement>(null); // Currently unused
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const loadOptions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getCategoryOptionsByType(
        selectedType,
        showInactive
      );
      setOptions(data);
      setError(null);
    } catch (err) {
      setError("Failed to load options");
      console.error("Options load error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedType, showInactive]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const handleSaveOption = async (option: CategoryOptionDto) => {
    try {
      if (option.id) {
        // Update existing option
        const updateData: UpdateCategoryOptionDto = {
          name: option.name,
          defaultNotes: option.defaultNotes,
          defaultUnitPrice: option.defaultUnitPrice,
          unit: option.unit,
          personsPerRoom: option.personsPerRoom,
          vatRate: option.vatRate,
          optionType: option.optionType,
          isActive: option.isActive,
          sortOrder: option.sortOrder,
          maxQuantity: option.maxQuantity,
          profitroomOfferId: option.profitroomOfferId,
          singleBeds: option.singleBeds,
          doubleBeds: option.doubleBeds,
          convertibleBeds: option.convertibleBeds,
          isAutoBreakfast: option.isAutoBreakfast,
        };
        await api.updateComponent(option.id, updateData);
      } else {
        // Create new option
        const { id, createdAt, updatedAt, hotelId, ...rest } = option;
        await api.createComponent(rest);
      }

      await loadOptions();
      setEditingOption(null);
      setError(null);
    } catch (err) {
      console.error("Option save error:", err);
      setError("Failed to save option");
    }
  };

  const handleActivateOption = async (optionId: string) => {
    try {
      await api.activateComponent(optionId);
      setOpenDropdown(null);
      loadOptions();
    } catch (err) {
      console.error("Activation error:", err);
      setError("Nie udało się aktywować składnika");
    }
  };

  const handleDeactivateClick = (option: CategoryOptionDto) => {
    setOptionToDeactivate(option);
    setShowDeactivateModal(true);
    setOpenDropdown(null);
  };

  const handleDeactivateConfirm = async () => {
    if (!optionToDeactivate) return;

    try {
      await api.deactivateComponent(optionToDeactivate.id);
      setShowDeactivateModal(false);
      setOptionToDeactivate(null);
      loadOptions();
    } catch (err) {
      console.error("Deactivation error:", err);
      setError("Nie udało się dezaktywować składnika");
    }
  };

  const calculateDropdownPosition = useCallback(
    (buttonElement: HTMLButtonElement): { top: number; left: number } => {
      const rect = buttonElement.getBoundingClientRect();
      const dropdownWidth = 192; // w-48 = 192px
      const dropdownHeight = 100; // More realistic height for 2 menu items

      let left = rect.right - dropdownWidth;
      let top = rect.bottom + 8; // mt-2 = 8px

      // Check if dropdown would go off the right edge
      if (left < 10) {
        left = rect.left;
      }

      // Check if dropdown would go off the bottom edge
      if (top + dropdownHeight > window.innerHeight - 10) {
        top = rect.top - dropdownHeight + 8;
      }

      // Ensure minimum distances from viewport edges
      left = Math.max(
        10,
        Math.min(left, window.innerWidth - dropdownWidth - 10)
      );
      top = Math.max(10, top);

      return { top, left };
    },
    []
  );

  const handleDropdownButtonClick = useCallback(
    (optionId: string) => {
      if (openDropdown === optionId) {
        setOpenDropdown(null);
        setDropdownPosition(null);
      } else {
        const buttonElement = buttonRefs.current[optionId];
        if (buttonElement) {
          const position = calculateDropdownPosition(buttonElement);
          setDropdownPosition(position);
          setOpenDropdown(optionId);
        }
      }
    },
    [openDropdown, calculateDropdownPosition]
  );

  const handleEditOption = (option: CategoryOptionDto) => {
    setEditingOption(option);
    setOpenDropdown(null);
    setDropdownPosition(null);
  };

  const handleNewOption = () => {
    setEditingOption({
      id: "",
      name: "",
      defaultNotes: "",
      defaultUnitPrice: 0,
      unit: null,
      personsPerRoom: selectedType === "accommodation" ? 2 : undefined,
      vatRate: 23,
      optionType: selectedType,
      isActive: true,
      sortOrder: 0,
      hotelId: "", // Backend will set this automatically
      createdAt: "",
      updatedAt: "",
    });
  };

  // Close dropdown when clicking outside or on scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is on a dropdown button
      const isDropdownButton = Object.values(buttonRefs.current).some(
        (button) => button && button.contains(target)
      );

      // Check if click is inside the portal dropdown
      const isInsideDropdown = document
        .getElementById("context-menu-portal")
        ?.contains(target);

      if (!isDropdownButton && !isInsideDropdown) {
        setOpenDropdown(null);
        setDropdownPosition(null);
      }
    };

    const handleScroll = () => {
      if (openDropdown) {
        setOpenDropdown(null);
        setDropdownPosition(null);
      }
    };

    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      const scrollContainer = document.querySelector(".overflow-x-auto");
      if (scrollContainer) {
        scrollContainer.addEventListener("scroll", handleScroll);
      }
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      const scrollContainer = document.querySelector(".overflow-x-auto");
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [openDropdown]);

  const currentCategory = CATEGORY_TYPES.find(
    (cat) => cat.value === selectedType
  );

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading options...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
          <div className="text-error-700">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Category Selection Panel */}
        <div className="lg:col-span-1 bg-white p-4 rounded shadow">
          <h2 className="text-xl font-semibold mb-4">Kategorie</h2>
          <div className="space-y-2">
            {CATEGORY_TYPES.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedType(category.value)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedType === category.value
                    ? "bg-primary-100 text-primary-800 border-2 border-primary-300"
                    : "bg-gray-50 text-gray-700 border-2 border-transparent hover:bg-gray-100"
                }`}
              >
                <div className="font-medium">{category.label}</div>
                <div className="text-sm text-gray-500 mt-1">
                  {category.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Options List Panel */}
        <div className="lg:col-span-3">
          <div className="bg-white p-6 rounded shadow">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold">
                  {currentCategory?.label}
                </h2>
                <p className="text-gray-600">{currentCategory?.description}</p>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    Pokaż nieaktywne
                  </span>
                </label>
                <button
                  onClick={handleNewOption}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Dodaj składnik
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nazwa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cena domyślna
                    </th>
                    {selectedType === "accommodation" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Osób/pokój
                      </th>
                    )}
                    {selectedType === "accommodation" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Profitroom ID
                      </th>
                    )}
                    {selectedType === "accommodation" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Łóżka (S/D/C)
                      </th>
                    )}
                    {selectedType === "gastronomy" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Auto śniadanie
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      VAT %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Max ilość
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {options.map((option) => (
                    <tr
                      key={option.id}
                      className={option.isActive ? "" : "opacity-50"}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-left text-sm font-medium text-gray-900">
                          {option.name}
                        </div>
                        {option.defaultNotes && (
                          <div className="text-sm text-gray-500">
                            {option.defaultNotes}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {option.defaultUnitPrice} zł
                      </td>
                      {selectedType === "accommodation" && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {option.personsPerRoom || "-"}
                        </td>
                      )}
                      {selectedType === "accommodation" && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                            {option.profitroomOfferId || "-"}
                          </span>
                        </td>
                      )}
                      {selectedType === "accommodation" && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {option.singleBeds ||
                          option.doubleBeds ||
                          option.convertibleBeds ? (
                            <span className="text-xs">
                              {option.singleBeds || 0}S /{" "}
                              {option.doubleBeds || 0}D /{" "}
                              {option.convertibleBeds || 0}C
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      {selectedType === "gastronomy" && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {option.isAutoBreakfast && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ☕ Auto
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {option.vatRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {option.maxQuantity && option.maxQuantity > 0 ? (
                          <span className="text-orange-600 font-medium">
                            {option.maxQuantity}
                          </span>
                        ) : (
                          <span className="text-gray-400">∞</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          ref={(el) => {
                            buttonRefs.current[option.id] = el;
                          }}
                          onClick={() => handleDropdownButtonClick(option.id)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {options.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Brak składników w tej kategorii. Kliknij "Dodaj składnik" aby
                  utworzyć pierwszy.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu Portal */}
      {openDropdown &&
        dropdownPosition &&
        createPortal(
          <div
            id="context-menu-portal"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
            }}
            className="w-48 bg-white rounded-md shadow-lg border border-gray-200"
          >
            <div className="py-1">
              <button
                onClick={() => {
                  const option = options.find((o) => o.id === openDropdown);
                  if (option) handleEditOption(option);
                }}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edytuj
              </button>

              {(() => {
                const option = options.find((o) => o.id === openDropdown);
                if (!option) return null;

                return option.isActive ? (
                  <button
                    onClick={() => handleDeactivateClick(option)}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                      />
                    </svg>
                    Dezaktywuj
                  </button>
                ) : (
                  <button
                    onClick={() => handleActivateOption(option.id)}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Aktywuj
                  </button>
                );
              })()}
            </div>
          </div>,
          document.body
        )}

      {/* Edit Option Modal */}
      {editingOption && (
        <OptionFormModal
          option={editingOption}
          onSave={handleSaveOption}
          onCancel={() => setEditingOption(null)}
        />
      )}

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && optionToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-orange-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">
                Dezaktywacja składnika
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Ta akcja spowoduje, że składnik{" "}
                <strong>"{optionToDeactivate.name}"</strong>:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Nie będzie widoczny przy tworzeniu nowych ofert</li>
                <li>
                  • Nie będzie uwzględniany przy pobieraniu cen z Profitroom
                </li>
              </ul>
              <p className="text-sm text-gray-600 mt-4">
                Istniejące oferty zawierające ten składnik pozostaną
                niezmienione.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setOptionToDeactivate(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={handleDeactivateConfirm}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Dezaktywuj
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

// Option Form Modal Component
interface OptionFormModalProps {
  option: CategoryOptionDto;
  onSave: (option: CategoryOptionDto) => void;
  onCancel: () => void;
}

const OptionFormModal: React.FC<OptionFormModalProps> = ({
  option,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CategoryOptionDto>(option);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof CategoryOptionDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {option.id ? "Edytuj składnik" : "Nowy składnik"}
        </h3>

        {option.id && (
          <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-primary-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-primary-800">
                Zmiany w tym składniku mogą wpłynąć na wyświetlanie istniejących
                ofert.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domyślne uwagi
            </label>
            <textarea
              value={formData.defaultNotes || ""}
              onChange={(e) => handleChange("defaultNotes", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Domyślny opis, który zostanie automatycznie dodany do planu dnia..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cena domyślna (zł) *
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.defaultUnitPrice}
              onChange={(e) =>
                handleChange(
                  "defaultUnitPrice",
                  parseFloat(e.target.value) || 0
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jednostka
            </label>
            <select
              value={formData.unit || ""}
              onChange={(e) =>
                handleChange("unit", e.target.value === "" ? null : e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— brak —</option>
              {UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Jednostka „os." automatycznie ustawia ilość równą liczbie gości.
            </p>
          </div>

          {formData.optionType === "accommodation" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Liczba osób na pokój
              </label>
              <input
                type="number"
                min="1"
                value={formData.personsPerRoom || ""}
                onChange={(e) =>
                  handleChange(
                    "personsPerRoom",
                    parseInt(e.target.value) || undefined
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          {formData.optionType === "accommodation" && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                🏨 Mapowanie Profitroom
              </h4>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profitroom Room ID
                  </label>
                  <input
                    type="text"
                    value={formData.profitroomOfferId || ""}
                    onChange={(e) =>
                      handleChange("profitroomOfferId", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                    placeholder="np. 9103, 44664"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    ID pokoju w systemie Profitroom (np. 9103 dla "Deluxe 1")
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Łóżka pojedyncze
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.singleBeds || ""}
                      onChange={(e) =>
                        handleChange(
                          "singleBeds",
                          e.target.value
                            ? parseInt(e.target.value) || 0
                            : undefined
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Łóżka podwójne
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.doubleBeds || ""}
                      onChange={(e) =>
                        handleChange(
                          "doubleBeds",
                          e.target.value
                            ? parseInt(e.target.value) || 0
                            : undefined
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Łóżka rozłączne
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.convertibleBeds || ""}
                      onChange={(e) =>
                        handleChange(
                          "convertibleBeds",
                          e.target.value
                            ? parseInt(e.target.value) || 0
                            : undefined
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  💡 Konfiguracja łóżek używana do automatycznego dopasowywania
                  dostępności z Profitroom. Łóżka rozłączne = można zrobić 2x
                  single lub 1x double.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stawka VAT (%) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.vatRate}
              onChange={(e) =>
                handleChange("vatRate", parseFloat(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maksymalna ilość
            </label>
            <input
              type="number"
              min="0"
              value={formData.maxQuantity || ""}
              onChange={(e) =>
                handleChange(
                  "maxQuantity",
                  e.target.value ? parseInt(e.target.value) || 0 : undefined
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Pozostaw puste lub 0 dla braku limitu"
            />
            <p className="mt-1 text-xs text-gray-500">
              Ustaw maksymalną liczbę tego składnika w ofercie. Pozostaw puste
              lub wpisz 0 dla braku ograniczenia.
            </p>
          </div>

          {formData.optionType === "gastronomy" && (
            <>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isAutoBreakfast"
                  checked={formData.isAutoBreakfast || false}
                  onChange={(e) =>
                    handleChange("isAutoBreakfast", e.target.checked)
                  }
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="isAutoBreakfast"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Automatyczne śniadanie
                </label>
              </div>

              {formData.isAutoBreakfast && (
                <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <p className="text-sm text-primary-800">
                    Ta opcja będzie automatycznie dodawana do każdego dnia
                    pobytu (oprócz check-in) z godziną 07:00-10:30 i ilością
                    równą liczbie gości.
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isActive"
              className="ml-2 block text-sm text-gray-900"
            >
              Aktywny
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Zapisz
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryOptionsManagement;
